import { HttpClient } from './http-client.ts';
import type { AxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from './endpoints.ts';
import { AppApiError } from '@core/errors/api-errors.ts';
import { ApiErrorCode } from '@core/types/api.types.ts';
import type { ApiConfig, ApiResponse } from '@core/types/api.types.ts';
import type {
  TableDataResponse,
  ChartDataResponse,
  PaginatedClickPalmApiResponse,
  DynamicTableRequestBody,
} from '@core/types/table.types.ts';
import type { LoginInputDTO, LoginOutputDTO, RenewTokenOutputDTO } from '@core/types/auth.types.ts';
import type { ActivityUsageRankingItem, ActivityModuleGroupDTO } from '@core/types/activity.types.ts';
import type {
  ActivityFullDTO,
  ActivityCreateInputDTO,
  ActivityUpdateInputDTO,
} from '@core/types/activity-builder.types.ts';
import type { ZoneByUsage } from '@core/types/zone.types.ts';
import type { ReportScheduleDTO, ReportTypeDTO, UpsertReportScheduleInputDTO } from '@core/types/report-scheduling.types.ts';
import type { SetZoneActivityCyclesInputDTO, ZoneActivityCyclesDTO } from '@core/types/zone-activity-cycles.types.ts';
import type { DocsAccessCheck, TechnicalDoc, TechnicalDocMeta } from '@core/types/docs.types.ts';
import type {
  UsersOutputDTO,
  UsersInputDTO,
  UsersCredentialsOutputDTO,
  UsersCredentialsInputDTO,
  UserSearchType,
  OperatorCreateInputDTO,
  OperatorUpdateInputDTO,
  PortalUserCreateInputDTO,
  PortalUserUpdateInputDTO,
} from '@core/types/users.types.ts';
import type {
  DevicesOutputDTO,
  DeviceCreateInputDTO,
  DeviceUpdateInputDTO,
} from '@core/types/devices.types.ts';
import type { DeactivateDeviceResult, DevicesDashboard } from '@core/types/devices-dashboard.types.ts';
import type { IaAskAssistantRequest, IaAskAssistantResponse } from '@core/types/ia-assistant.types.ts';
import type { IaAskRequest, IaAskResponse } from '@core/types/ia-ask.types.ts';
import type { IaChatGeneralRequest, IaChatGeneralResponse } from '@core/types/ia-chat-general.types.ts';
import type {
  IaThreadListResponse,
  IaThreadDetailDTO,
  IaThreadSummaryDTO,
  IaThreadCreateRequest,
  IaThreadAppendRequest,
  IaThreadAppendResponse,
  IaThreadPatchRequest,
  IaThreadDeleteResponse,
} from '@core/types/ia-thread.types.ts';
import { tokenStorage } from '@infra/storage/token-storage.ts';
import type {
  OldActivityDTO,
  OldActivityStatusDTO,
  OldSurveyHeaderDTO,
  OldSurveyDetailDTO,
  OldActivityHierarchyDTO,
  LegacyActivityStatusOutputDTO,
  SurveyMigrationResultDTO,
} from '@core/types/old-activity.types.ts';
import type {
  SyncCountsByMonthRequest,
  SyncCountsByMonthResponse,
  SyncCountsByDayRequest,
  SyncCountsByDayResponse,
  SyncFullAsyncRequest,
  SyncFullAsyncResponse,
  SyncStatusResponse,
  DeleteByPeriodRequest,
  SyncTrackingStatsByDateResponse,
  ReprocessMissingVisitsByDateResponse,
} from '@core/types/migration.types.ts';
import type {
  HeadquarterDTO,
  HeadquarterWeightDTO,
  GetYearlyWeightsParams,
  CreateHeadquarterWeightInput,
  UpdateHeadquarterWeightInput,
} from '@core/types/headquarters-weights.types.ts';
import type {
  ReportsInputDTO,
  ReportsOutputDTO,
} from '@core/types/reports.types.ts';
import type {
  DashboardMetricOutputDTO,
  DashboardMetricFilterOutputDTO,
  DashboardMetricItemOutputDTO,
  CreateMetricDto,
  UpdateMetricDto,
  CreateMetricFilterDto,
  UpdateMetricFilterDto,
  CreateMetricItemDto,
  UpdateMetricItemDto,
} from '@core/types/dashboard-metrics.types.ts';

// ═══════════════════════════════════════════
// SINGLETON FACTORY
// ═══════════════════════════════════════════

let apiInstance: ApiService | null = null;

export function createApiService(config: ApiConfig): ApiService {
  apiInstance = new ApiService(config);
  return apiInstance;
}

export function getApiService(): ApiService {
  if (!apiInstance) {
    throw new Error('ApiService no ha sido inicializado. Llama createApiService() primero.');
  }
  return apiInstance;
}

// ═══════════════════════════════════════════
// API SERVICE
// ═══════════════════════════════════════════

/**
 * La forma en la que el API sirve una guía técnica.
 *
 * El API responde en snake_case, así que `reading_minutes` no encaja con `readingMinutes`
 * del tipo de dominio. Mientras el front leyó la clave equivocada, la cabecera de todas las
 * guías internas mostraba «min de lectura» sin número delante.
 */
interface RawTechnicalDocMeta extends Omit<TechnicalDocMeta, 'readingMinutes'> {
  reading_minutes: number;
}

interface RawTechnicalDoc extends RawTechnicalDocMeta {
  body: string;
}

function toTechnicalDocMeta(raw: RawTechnicalDocMeta): TechnicalDocMeta {
  const { reading_minutes, ...rest } = raw;
  return { ...rest, readingMinutes: reading_minutes };
}

export class ApiService {
  private http: HttpClient;

  constructor(config: ApiConfig) {
    this.http = new HttpClient(config);
  }

  // ──── Auth ────
  async login(credentials: Pick<LoginInputDTO, 'username' | 'password'>): Promise<LoginOutputDTO> {
    const body: LoginInputDTO = { type_login: '', username: credentials.username, password: credentials.password };
    const response = await this.http.post<LoginOutputDTO>(API_ENDPOINTS.AUTH.LOGIN, body);
    tokenStorage.setRawJwt(response.data.token);
    return response.data;
  }

  async renewToken(credentials: Pick<LoginInputDTO, 'username' | 'password'>): Promise<RenewTokenOutputDTO> {
    const body: LoginInputDTO = { type_login: '', username: credentials.username, password: credentials.password };
    const response = await this.http.post<RenewTokenOutputDTO>(API_ENDPOINTS.AUTH.RENEW_TOKEN, body);
    tokenStorage.setRawJwt(response.data.token);
    return response.data;
  }

  logout() {
    this.http.auth?.logout();
  }

  // ──── ClickPalm Dynamic Table ────
  async generateDynamicTable(
    body: DynamicTableRequestBody,
    signal?: AbortSignal,
  ): Promise<ApiResponse<PaginatedClickPalmApiResponse>> {
    return this.http.post<PaginatedClickPalmApiResponse>(
      '/DynamicTable/generate',
      body,
      { timeout: 300_000, signal }, // 5 min timeout like original JS
    );
  }

  // ──── Table Data (generic) ────
  async getTableData(
    endpoint: string,
    params?: {
      page?: number;
      pageSize?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      filters?: Record<string, unknown>;
      search?: string;
    },
  ): Promise<ApiResponse<TableDataResponse>> {
    return this.http.get<TableDataResponse>(endpoint, { params });
  }

  async getTableConfig(endpoint: string) {
    return this.http.get(endpoint);
  }

  // ──── Chart Data ────
  async getChartData(
    endpoint: string,
    params?: Record<string, unknown>,
  ): Promise<ApiResponse<ChartDataResponse>> {
    return this.http.get<ChartDataResponse>(endpoint, { params });
  }

  // ──── Ia Assistant ────
  async askAssistant(
    assistantId: string,
    body: IaAskAssistantRequest,
  ): Promise<ApiResponse<IaAskAssistantResponse>> {
    return this.http.post<IaAskAssistantResponse>(API_ENDPOINTS.IA.ASSISTANT_ASK(assistantId), body);
  }

  async askData(body: IaAskRequest): Promise<ApiResponse<IaAskResponse>> {
    return this.http.post<IaAskResponse>(API_ENDPOINTS.IA.ASK, body, { timeout: 120_000 });
  }

  /**
   * El minuto por defecto vale para una pregunta suelta.
   *
   * Cuando la pregunta lleva dentro el informe de una pantalla, el modelo tarda bastante más y
   * quien llama tiene que poder ampliarlo. El API tolera tres minutos.
   */
  async chatGeneral(
    body: IaChatGeneralRequest,
    options?: { timeoutMs?: number },
  ): Promise<ApiResponse<IaChatGeneralResponse>> {
    return this.http.post<IaChatGeneralResponse>(
      API_ENDPOINTS.IA.CHAT_GENERAL,
      body,
      { timeout: options?.timeoutMs ?? 60_000 },
    );
  }

  /** Vacía la caché de resultados de datos de la empresa del usuario (botón "Refrescar"). */
  async refreshMetricCache(): Promise<ApiResponse<{ borradas: number }>> {
    return this.http.post<{ borradas: number }>(API_ENDPOINTS.IA.REFRESH_METRIC_CACHE);
  }

  // ──── Ia Threads (historial guardado del asistente) ────
  async getIaThreads(limit: number, offset: number): Promise<ApiResponse<IaThreadListResponse>> {
    return this.http.get<IaThreadListResponse>(API_ENDPOINTS.IA.THREADS, { params: { limit, offset } });
  }

  async getIaThread(id: number): Promise<ApiResponse<IaThreadDetailDTO>> {
    return this.http.get<IaThreadDetailDTO>(API_ENDPOINTS.IA.THREAD(id));
  }

  async createIaThread(body: IaThreadCreateRequest): Promise<ApiResponse<IaThreadSummaryDTO>> {
    return this.http.post<IaThreadSummaryDTO>(API_ENDPOINTS.IA.THREADS, body);
  }

  async appendIaThreadMessages(
    id: number,
    body: IaThreadAppendRequest,
  ): Promise<ApiResponse<IaThreadAppendResponse>> {
    return this.http.post<IaThreadAppendResponse>(API_ENDPOINTS.IA.THREAD_MESSAGES(id), body);
  }

  async patchIaThread(id: number, body: IaThreadPatchRequest): Promise<ApiResponse<IaThreadSummaryDTO>> {
    return this.http.patch<IaThreadSummaryDTO>(API_ENDPOINTS.IA.THREAD(id), body);
  }

  async deleteIaThread(id: number): Promise<ApiResponse<IaThreadDeleteResponse>> {
    return this.http.delete<IaThreadDeleteResponse>(API_ENDPOINTS.IA.THREAD(id));
  }

  // ──── Dashboard Metrics ────
  async getDashboardsByCompany(companyId: number): Promise<ApiResponse<DashboardMetricOutputDTO[]>> {
    return this.http.get<DashboardMetricOutputDTO[]>(API_ENDPOINTS.DASHBOARD_METRICS.BY_COMPANY(companyId));
  }

  async getMetricsByDashboard(dashboardId: number): Promise<ApiResponse<DashboardMetricOutputDTO[]>> {
    return this.http.get<DashboardMetricOutputDTO[]>(API_ENDPOINTS.DASHBOARD_METRICS.BY_DASHBOARD(dashboardId));
  }

  async getMetricById(id: number): Promise<ApiResponse<DashboardMetricOutputDTO>> {
    return this.http.get<DashboardMetricOutputDTO>(API_ENDPOINTS.DASHBOARD_METRICS.BY_ID(id));
  }

  async getMetricWithDetails(id: number): Promise<ApiResponse<DashboardMetricOutputDTO>> {
    return this.http.get<DashboardMetricOutputDTO>(API_ENDPOINTS.DASHBOARD_METRICS.WITH_DETAILS(id));
  }

  async createMetric(data: CreateMetricDto): Promise<ApiResponse<DashboardMetricOutputDTO>> {
    return this.http.post<DashboardMetricOutputDTO>(API_ENDPOINTS.DASHBOARD_METRICS.CREATE, data);
  }

  async updateMetric(id: number, data: UpdateMetricDto): Promise<ApiResponse<DashboardMetricOutputDTO>> {
    return this.http.put<DashboardMetricOutputDTO>(API_ENDPOINTS.DASHBOARD_METRICS.UPDATE(id), data);
  }

  async deleteMetric(id: number, userId: number): Promise<ApiResponse<void>> {
    return this.http.delete<void>(API_ENDPOINTS.DASHBOARD_METRICS.DELETE(id, userId));
  }

  // ──── Metric Filters ────
  async addMetricFilter(metricId: number, data: CreateMetricFilterDto): Promise<ApiResponse<DashboardMetricFilterOutputDTO>> {
    return this.http.post<DashboardMetricFilterOutputDTO>(API_ENDPOINTS.DASHBOARD_METRICS.FILTERS.ADD(metricId), data);
  }

  async updateMetricFilter(filterId: number, data: UpdateMetricFilterDto): Promise<ApiResponse<DashboardMetricFilterOutputDTO>> {
    return this.http.put<DashboardMetricFilterOutputDTO>(API_ENDPOINTS.DASHBOARD_METRICS.FILTERS.UPDATE(filterId), data);
  }

  async deleteMetricFilter(filterId: number): Promise<ApiResponse<void>> {
    return this.http.delete<void>(API_ENDPOINTS.DASHBOARD_METRICS.FILTERS.DELETE(filterId));
  }

  // ──── Metric Items ────
  async addMetricItem(metricId: number, data: CreateMetricItemDto): Promise<ApiResponse<DashboardMetricItemOutputDTO>> {
    return this.http.post<DashboardMetricItemOutputDTO>(API_ENDPOINTS.DASHBOARD_METRICS.ITEMS.ADD(metricId), data);
  }

  async updateMetricItem(itemId: number, data: UpdateMetricItemDto): Promise<ApiResponse<DashboardMetricItemOutputDTO>> {
    return this.http.put<DashboardMetricItemOutputDTO>(API_ENDPOINTS.DASHBOARD_METRICS.ITEMS.UPDATE(itemId), data);
  }

  async deleteMetricItem(itemId: number): Promise<ApiResponse<void>> {
    return this.http.delete<void>(API_ENDPOINTS.DASHBOARD_METRICS.ITEMS.DELETE(itemId));
  }

  // ──── Activities ────
  async getActivityUsageRanking(companyId: number): Promise<ActivityUsageRankingItem[]> {
    const response = await this.fetchMany<ActivityUsageRankingItem>(
      API_ENDPOINTS.ACTIVITIES.USAGE_RANKING,
      { id_company: companyId },
    );
    return response.data;
  }

  async getActivitiesGroupedByModule(
    companyId: number,
    includeInactive = false,
  ): Promise<ActivityModuleGroupDTO[]> {
    const response = await this.fetchMany<ActivityModuleGroupDTO>(
      API_ENDPOINTS.ACTIVITIES.GROUPED_BY_MODULE,
      { id_company: companyId, includeInactive },
    );
    return response.data;
  }

  async updateActivityActiveStatus(id: number, isActive: boolean): Promise<void> {
    await this.http.patch<void>(API_ENDPOINTS.ACTIVITIES.UPDATE_ACTIVE_STATUS(id), isActive);
  }

  async getReportTypes(): Promise<ReportTypeDTO[]> {
    const response = await this.http.get<ReportTypeDTO[]>(API_ENDPOINTS.REPORT_SCHEDULING.TYPES);
    return response.data;
  }

  async getReportSchedulesForCompany(companyId: number): Promise<ReportScheduleDTO[]> {
    const response = await this.http.get<ReportScheduleDTO[]>(API_ENDPOINTS.REPORT_SCHEDULING.LIST_BY_COMPANY(companyId));
    return response.data;
  }

  async getReportSchedule(companyId: number, reportType: string): Promise<ReportScheduleDTO | null> {
    const response = await this.http.get<ReportScheduleDTO | null>(API_ENDPOINTS.REPORT_SCHEDULING.BY_COMPANY_AND_TYPE(companyId, reportType));
    return response.data;
  }

  async upsertReportSchedule(companyId: number, reportType: string, input: UpsertReportScheduleInputDTO): Promise<ReportScheduleDTO> {
    const response = await this.http.put<ReportScheduleDTO>(API_ENDPOINTS.REPORT_SCHEDULING.BY_COMPANY_AND_TYPE(companyId, reportType), input);
    return response.data;
  }

  async testSendReportSchedule(companyId: number, reportType: string, fecha?: string): Promise<{ success: boolean; detail: string }> {
    const base = API_ENDPOINTS.REPORT_SCHEDULING.TEST_SEND(companyId, reportType);
    const url = fecha ? `${base}?fecha=${encodeURIComponent(fecha)}` : base;
    const response = await this.http.post<{ success: boolean; detail: string }>(url, {});
    return response.data;
  }

  async getZoneActivityCycles(companyId: number, activityId: number, month: string): Promise<ZoneActivityCyclesDTO> {
    const response = await this.http.get<ZoneActivityCyclesDTO>(
      API_ENDPOINTS.ZONE_ACTIVITY_CYCLES.BY_COMPANY_AND_ACTIVITY(companyId, activityId),
      { params: { month } },
    );
    return response.data;
  }

  async setZoneActivityCycles(companyId: number, activityId: number, input: SetZoneActivityCyclesInputDTO): Promise<ZoneActivityCyclesDTO> {
    const response = await this.http.put<ZoneActivityCyclesDTO>(
      API_ENDPOINTS.ZONE_ACTIVITY_CYCLES.BY_COMPANY_AND_ACTIVITY(companyId, activityId),
      input,
    );
    return response.data;
  }

  async getActivityById(id: number): Promise<ActivityFullDTO> {
    const response = await this.http.get<ActivityFullDTO>(API_ENDPOINTS.ACTIVITIES.BY_ID(id));
    return response.data;
  }

  async createActivity(body: ActivityCreateInputDTO): Promise<ActivityFullDTO> {
    const response = await this.http.post<ActivityFullDTO>(API_ENDPOINTS.ACTIVITIES.CREATE, body);
    return response.data;
  }

  async updateActivity(id: number, body: ActivityUpdateInputDTO): Promise<ActivityFullDTO> {
    const response = await this.http.put<ActivityFullDTO>(API_ENDPOINTS.ACTIVITIES.UPDATE(id), body);
    return response.data;
  }

  async deleteActivity(id: number): Promise<void> {
    await this.http.delete<void>(API_ENDPOINTS.ACTIVITIES.DELETE(id));
  }

  // ──── Old Activities (legacy DB lookup) ────
  async getOldActivityById(id: number): Promise<OldActivityDTO> {
    const r = await this.http.get<OldActivityDTO>(API_ENDPOINTS.OLD_ACTIVITIES.BY_ID(id));
    return r.data;
  }

  async getOldActivityStatuses(id: number): Promise<OldActivityStatusDTO[]> {
    const r = await this.http.get<OldActivityStatusDTO[]>(API_ENDPOINTS.OLD_ACTIVITIES.STATUS(id));
    return r.data;
  }

  async getOldActivitySurveysHeaders(id: number): Promise<OldSurveyHeaderDTO[]> {
    const r = await this.http.get<OldSurveyHeaderDTO[]>(API_ENDPOINTS.OLD_ACTIVITIES.SURVEYS_HEADERS(id));
    return r.data;
  }

  async getOldSurveyDetails(surveyId: number): Promise<OldSurveyDetailDTO[]> {
    const r = await this.http.get<OldSurveyDetailDTO[]>(API_ENDPOINTS.OLD_ACTIVITIES.SURVEY_DETAILS(surveyId));
    return r.data;
  }

  /** Árbol jerárquico completo (steps + status anidados) de una actividad legacy. */
  async getOldActivityHierarchy(id: number): Promise<OldActivityHierarchyDTO> {
    const r = await this.http.get<OldActivityHierarchyDTO>(API_ENDPOINTS.OLD_ACTIVITIES.HIERARCHY(id));
    return r.data;
  }

  /**
   * Busca la actividad legacy y sus statuses por nombre exacto dentro de una
   * compañía vieja. Devuelve `null` cuando no hay coincidencia (404), de modo
   * que la auto-sugerencia trate "sin match" como caso normal, no como error.
   */
  async getLegacyStatusByName(
    activityName: string,
    oldCompanyId: number,
  ): Promise<LegacyActivityStatusOutputDTO | null> {
    try {
      const r = await this.http.get<LegacyActivityStatusOutputDTO>(
        API_ENDPOINTS.ACTIVITIES.LEGACY_STATUS_BY_NAME(activityName, oldCompanyId),
      );
      return r.data;
    } catch (err) {
      if (err instanceof AppApiError && err.code === ApiErrorCode.NOT_FOUND) return null;
      throw err;
    }
  }

  /**
   * Clona un formulario (Survey) legacy hacia la actividad nueva: el servidor
   * busca la actividad+survey vieja, crea o actualiza la actividad nueva por
   * nombre+compañía y genera sus steps/status a partir de `Surveys_Details`,
   * dejando los vínculos `id_activity_old` / `id_activity_step_old` /
   * `id_activity_status_old`. En fallo (400) el body trae `success:false` y
   * `message`, que `AppApiError.message` ya expone.
   */
  async migrateSurveyFromLegacy(
    oldActivityId: number,
    oldCompanyId: number,
  ): Promise<SurveyMigrationResultDTO> {
    const r = await this.http.post<SurveyMigrationResultDTO>(
      API_ENDPOINTS.ACTIVITIES.MIGRATE_SURVEY(oldActivityId, oldCompanyId),
    );
    return r.data;
  }

  // ──── Zones ────
  async getZonesByCompanyUsage(companyId: number): Promise<ZoneByUsage[]> {
    const response = await this.http.get<ZoneByUsage[]>(
      API_ENDPOINTS.ZONES.BY_COMPANY_USAGE(companyId),
    );
    return response.data;
  }

  // ──── Users ────
  async getUsers(companyId: number, type: UserSearchType): Promise<ApiResponse<UsersOutputDTO[]>> {
    return this.http.get<UsersOutputDTO[]>(API_ENDPOINTS.USERS.FILTERS, {
      params: { Type_search: type, Id_company: companyId },
    });
  }

  async getUserById(id: number): Promise<ApiResponse<UsersOutputDTO>> {
    return this.http.get<UsersOutputDTO>(API_ENDPOINTS.USERS.BY_ID(id));
  }

  async createUser(data: UsersInputDTO): Promise<ApiResponse<UsersOutputDTO>> {
    return this.http.post<UsersOutputDTO>(API_ENDPOINTS.USERS.CREATE, data);
  }

  async updateUser(id: number, data: UsersInputDTO): Promise<ApiResponse<UsersOutputDTO>> {
    return this.http.put<UsersOutputDTO>(API_ENDPOINTS.USERS.UPDATE(id), data);
  }

  async deleteUser(id: number): Promise<ApiResponse<void>> {
    return this.http.delete<void>(API_ENDPOINTS.USERS.DELETE(id));
  }

  async activateUser(id: number): Promise<ApiResponse<void>> {
    return this.http.put<void>(API_ENDPOINTS.USERS.ACTIVATE(id), {});
  }

  async deactivateUser(id: number): Promise<ApiResponse<void>> {
    return this.http.put<void>(API_ENDPOINTS.USERS.DEACTIVATE(id), {});
  }

  async createOperatorUser(data: OperatorCreateInputDTO): Promise<ApiResponse<UsersOutputDTO>> {
    return this.http.post<UsersOutputDTO>(API_ENDPOINTS.USERS.CREATE_OPERATOR, data);
  }

  async updateOperatorUser(id: number, data: OperatorUpdateInputDTO): Promise<ApiResponse<UsersOutputDTO>> {
    return this.http.put<UsersOutputDTO>(API_ENDPOINTS.USERS.UPDATE_OPERATOR(id), data);
  }

  async createPortalUser(data: PortalUserCreateInputDTO): Promise<ApiResponse<UsersOutputDTO>> {
    return this.http.post<UsersOutputDTO>(API_ENDPOINTS.USERS.CREATE_PORTAL, data);
  }

  async updatePortalUser(id: number, data: PortalUserUpdateInputDTO): Promise<ApiResponse<UsersOutputDTO>> {
    return this.http.put<UsersOutputDTO>(API_ENDPOINTS.USERS.UPDATE_PORTAL(id), data);
  }

  // ──── Credentials ────
  async getUserCredentials(userId: number, companyId: number): Promise<ApiResponse<UsersCredentialsOutputDTO[]>> {
    return this.http.get<UsersCredentialsOutputDTO[]>(API_ENDPOINTS.USERS_CREDENTIALS.FILTERS, {
      params: { Type_search: 'ID USER', Id_company: companyId, Text_search1: String(userId) },
    });
  }

  async createUserCredentials(data: UsersCredentialsInputDTO): Promise<ApiResponse<UsersCredentialsOutputDTO>> {
    return this.http.post<UsersCredentialsOutputDTO>(API_ENDPOINTS.USERS_CREDENTIALS.CREATE, data);
  }

  async updateUserCredentials(id: number, data: UsersCredentialsInputDTO): Promise<ApiResponse<UsersCredentialsOutputDTO>> {
    return this.http.put<UsersCredentialsOutputDTO>(API_ENDPOINTS.USERS_CREDENTIALS.UPDATE(id), data);
  }

  // ──── Devices ────
  async getDevices(companyId: number): Promise<ApiResponse<DevicesOutputDTO[]>> {
    // Sin includeInactive la API omite los inactivos, que es lo que quiere la app, no el admin
    return this.http.get<DevicesOutputDTO[]>(API_ENDPOINTS.DEVICES.FILTERS, {
      params: { Type_search: 'ID COMPANY', Id_company: companyId, includeInactive: true },
    });
  }

  /** Inactiva el CTR si tiene visitas; si no tiene, la API lo borra. */
  async deactivateDevice(id: number): Promise<ApiResponse<DeactivateDeviceResult>> {
    return this.http.post<DeactivateDeviceResult>(API_ENDPOINTS.DEVICES.DEACTIVATE(id), {});
  }

  async createDevice(data: DeviceCreateInputDTO): Promise<ApiResponse<DevicesOutputDTO>> {
    return this.http.post<DevicesOutputDTO>(API_ENDPOINTS.DEVICES.CREATE, data);
  }

  async updateDevice(id: number, data: DeviceUpdateInputDTO): Promise<ApiResponse<DevicesOutputDTO>> {
    return this.http.put<DevicesOutputDTO>(API_ENDPOINTS.DEVICES.UPDATE(id), data);
  }

  async deleteDevice(id: number): Promise<ApiResponse<void>> {
    return this.http.delete<void>(API_ENDPOINTS.DEVICES.DELETE(id));
  }

  /** `refresh` hace que la API ignore su caché de Redis y la reescriba. */
  async getDevicesDashboard(companyId: number, refresh = false): Promise<ApiResponse<DevicesDashboard>> {
    return this.http.get<DevicesDashboard>(API_ENDPOINTS.DEVICES.DASHBOARD, {
      params: { companyId, refresh },
    });
  }

  // ──── Visits / Migration ────
  async syncCountsByMonth(body: SyncCountsByMonthRequest): Promise<ApiResponse<SyncCountsByMonthResponse>> {
    return this.http.post<SyncCountsByMonthResponse>(API_ENDPOINTS.VISITS.SYNC_COUNTS_BY_MONTH, body);
  }

  async syncCountsByDay(body: SyncCountsByDayRequest): Promise<ApiResponse<SyncCountsByDayResponse>> {
    return this.http.post<SyncCountsByDayResponse>(API_ENDPOINTS.VISITS.SYNC_COUNTS_BY_DAY, body);
  }

  async syncFullV3Async(body: SyncFullAsyncRequest): Promise<ApiResponse<SyncFullAsyncResponse>> {
    return this.http.post<SyncFullAsyncResponse>(API_ENDPOINTS.VISITS.SYNC_FULL_V3_ASYNC, body);
  }

  async getSyncStatus(jobId: string): Promise<ApiResponse<SyncStatusResponse>> {
    return this.http.get<SyncStatusResponse>(API_ENDPOINTS.VISITS.SYNC_STATUS(jobId));
  }

  async deleteVisitsByPeriod(body: DeleteByPeriodRequest): Promise<ApiResponse<unknown>> {
    return this.http.post<unknown>(API_ENDPOINTS.VISITS.DELETE_BY_PERIOD, body);
  }

  async getSyncTrackingStatsByDate(date: string, idCompany: number): Promise<ApiResponse<SyncTrackingStatsByDateResponse>> {
    return this.http.get<SyncTrackingStatsByDateResponse>(API_ENDPOINTS.SYNC_TIMES.STATS_BY_DATE(date, idCompany));
  }

  async reprocessMissingVisitsByDate(date: string, idCompany: number, callbackUrl?: string): Promise<ApiResponse<ReprocessMissingVisitsByDateResponse>> {
    return this.http.post<ReprocessMissingVisitsByDateResponse>(
      API_ENDPOINTS.SYNC_TIMES.REPROCESS_BY_DATE(date, idCompany, callbackUrl),
    );
  }

  async reprocessSyncTracking(id: number): Promise<ApiResponse<unknown>> {
    // Un POST que encola trabajo no debe repetirse por el retry HTTP automatico.
    const config: AxiosRequestConfig & { _skipRetry: boolean } = { _skipRetry: true };
    return this.http.post(API_ENDPOINTS.SYNC_TIMES.REPROCESS_TRACKING(id), undefined, config);
  }

  async deleteVisitsBulkByPeriod(body: DeleteByPeriodRequest): Promise<ApiResponse<unknown>> {
    return this.http.post<unknown>(API_ENDPOINTS.VISITS_BULK.DELETE_BY_PERIOD, body);
  }

  // ──── Headquarters Weights ────
  async getHeadquartersYearlyWeights(params: GetYearlyWeightsParams): Promise<ApiResponse<HeadquarterDTO[]>> {
    return this.http.get<HeadquarterDTO[]>(API_ENDPOINTS.HEADQUARTERS.YEARLY_WEIGHTS, { params });
  }

  async createHeadquarterWeight(body: CreateHeadquarterWeightInput): Promise<ApiResponse<HeadquarterWeightDTO>> {
    return this.http.post<HeadquarterWeightDTO>(API_ENDPOINTS.HEADQUARTERS.WEIGHTS_CREATE, body);
  }

  async updateHeadquarterWeight(id: number, body: UpdateHeadquarterWeightInput): Promise<ApiResponse<HeadquarterWeightDTO>> {
    return this.http.put<HeadquarterWeightDTO>(API_ENDPOINTS.HEADQUARTERS.WEIGHTS_UPDATE(id), body);
  }

  // ──── Reports ────
  async getReportsByCompany(companyId: number): Promise<ApiResponse<ReportsOutputDTO[]>> {
    return this.http.get<ReportsOutputDTO[]>(API_ENDPOINTS.REPORTS.BY_COMPANY(companyId));
  }

  async createReport(body: ReportsInputDTO): Promise<ApiResponse<ReportsOutputDTO>> {
    return this.http.post<ReportsOutputDTO>(API_ENDPOINTS.REPORTS.CREATE, body);
  }

  async updateReport(id: number, body: ReportsInputDTO): Promise<ApiResponse<ReportsOutputDTO>> {
    return this.http.put<ReportsOutputDTO>(API_ENDPOINTS.REPORTS.UPDATE(id), body);
  }

  async deleteReport(id: number): Promise<ApiResponse<void>> {
    return this.http.delete<void>(API_ENDPOINTS.REPORTS.DELETE(id));
  }

  async reloadReportScheduler(): Promise<ApiResponse<void>> {
    return this.http.post<void>(API_ENDPOINTS.REPORTS.RELOAD, {});
  }

  // ──── Generic CRUD ────
  async fetchOne<T>(endpoint: string, id: string | number): Promise<ApiResponse<T>> {
    return this.http.get<T>(`${endpoint}/${id}`);
  }

  async fetchMany<T>(
    endpoint: string,
    params?: Record<string, unknown>,
  ): Promise<ApiResponse<T[]>> {
    return this.http.get<T[]>(endpoint, { params });
  }

  async create<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.http.post<T>(endpoint, data);
  }

  async update<T>(
    endpoint: string,
    id: string | number,
    data: unknown,
  ): Promise<ApiResponse<T>> {
    return this.http.put<T>(`${endpoint}/${id}`, data);
  }

  async remove(endpoint: string, id: string | number): Promise<ApiResponse<void>> {
    return this.http.delete<void>(`${endpoint}/${id}`);
  }

  // ──── Documentación técnica interna ────
  // El contenido lo sirve el API, no el bundle: exige el JWT normal más un código de
  // operador con permiso ADMINISTRADOR en la cabecera X-Access-Code.

  async validateDocsCode(code: string): Promise<DocsAccessCheck> {
    const response = await this.http.post<DocsAccessCheck>(
      API_ENDPOINTS.TECHNICAL_DOCS.VALIDATE,
      { code },
    );
    return response.data;
  }

  async getTechnicalDocs(code: string): Promise<TechnicalDocMeta[]> {
    const response = await this.http.get<RawTechnicalDocMeta[]>(
      API_ENDPOINTS.TECHNICAL_DOCS.LIST,
      { headers: { 'X-Access-Code': code } },
    );
    return response.data.map(toTechnicalDocMeta);
  }

  async getTechnicalDoc(slug: string, code: string): Promise<TechnicalDoc> {
    const response = await this.http.get<RawTechnicalDoc>(
      API_ENDPOINTS.TECHNICAL_DOCS.BY_SLUG(slug),
      { headers: { 'X-Access-Code': code } },
    );
    return { ...toTechnicalDocMeta(response.data), body: response.data.body };
  }

  get client(): HttpClient {
    return this.http;
  }
}
